import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { Tooltip, Popover } from '@mui/material';
import { CmLink } from "../components/common/CmLink";

// Assume these icons are imported from an icon library
import { ChevronDownIcon, ChevronLeftIcon, AngleRightIcon, HorizontaLDots } from "../icons";
import { LayoutGrid, ShieldCheck, Bell, FileText, ChartColumn, MessagesSquare, PenLine, Search, Building2, Plug, Zap, Receipt, KeyRound } from "lucide-react";
import { useSidebar } from "../context/SidebarContext";
import { useStateValue } from "../context/StateValueContext";
import { useDisputesVisible } from "../hooks/useDisputesAccess";
import { useOrderInsightsVisible } from "../hooks/useOrderInsightsAccess";
import { PERMISSIONS } from "../constants/permissions";

type SubItem = { name: string; path: string; pro?: boolean; new?: boolean; permission?: string | string[]; section?: string };

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  private?: boolean;
  permission?: string | string[];
  requiresDisputesVisible?: boolean;
  requiresOrderInsightsVisible?: boolean;
  subItems?: SubItem[];
};

// Routes that accept either admin (ALERTS_ALL) or client (ALERTS_OWN) access.
const ALERTS_SHARED = [PERMISSIONS.ALERTS_ALL, PERMISSIONS.ALERTS_OWN];

const navItems: NavItem[] = [
  {
    icon: <LayoutGrid />,
    name: "Dashboard",
    path: "/dashboard",
    permission: PERMISSIONS.DASHBOARD,
  },
  {
    icon: <ShieldCheck />,
    name: "Deflections",
    permission: [PERMISSIONS.OI_ALL, PERMISSIONS.ALERTS_ALL, PERMISSIONS.OI_OWN],
    requiresOrderInsightsVisible: true,
    subItems: [
      { name: "C.E. Notices", path: "/alerts/ce-notices", permission: PERMISSIONS.ALERTS_ALL },
      { name: "Order Insights", path: "/alerts/order-insight", permission: [PERMISSIONS.OI_ALL, PERMISSIONS.OI_OWN] },
    ],
  },
  {
    icon: <Bell />,
    name: "Preventions",
    permission: [PERMISSIONS.ALERTS_OWN, PERMISSIONS.ALERTS_ALL, PERMISSIONS.ALERTS_SETTINGS],
    subItems: [
      // Section overview (lead, no section header)
      { name: "Overview", path: "/alerts/dashboard", permission: PERMISSIONS.ALERTS_ALL },
      // Alert feeds
      { name: "Ethoca Alerts", path: "/alerts/ethoca", permission: ALERTS_SHARED, section: "Alerts" },
      { name: "CDRN Alerts", path: "/alerts/cdrn", permission: ALERTS_SHARED, section: "Alerts" },
      { name: "RDR Alerts", path: "/alerts/rdr-notices", permission: ALERTS_SHARED, section: "Alerts" },
      { name: "Fraud Notices", path: "/alerts/fraud-notices", permission: ALERTS_SHARED, section: "Alerts" },
      { name: "Dispute Notices", path: "/alerts/dispute-notices", permission: ALERTS_SHARED, section: "Alerts" },
      // Provider configuration
      { name: "Ethoca Settings", path: "/alerts/ethoca-settings", permission: PERMISSIONS.ALERTS_SETTINGS, section: "Configuration" },
      { name: "CDRN Settings", path: "/alerts/cdrn-settings", permission: PERMISSIONS.ALERTS_SETTINGS, section: "Configuration" },
      { name: "Notices Settings", path: "/alerts/verifi-notices-settings", permission: PERMISSIONS.ALERTS_SETTINGS, section: "Configuration" },
      // Operational tools
      { name: "Alert Logs", path: "/alerts/logs", permission: PERMISSIONS.ALERTS_ALL, section: "Tools" },
      { name: "Verifi Import", path: "/verifi-import", permission: PERMISSIONS.ALERTS_SETTINGS, section: "Tools" },
      { name: "Enrolments", path: "/alerts/enrolments", permission: [PERMISSIONS.ENROLMENT_READ_ALL, PERMISSIONS.ENROLMENT_WRITE_ALL, PERMISSIONS.ENROLMENT_OWN], section: "Tools" },
      { name: "Billing Credits", path: "/alerts/billing-credits", permission: PERMISSIONS.BILLING_ALL, section: "Tools" },
    ],
  },
  {
    icon: <FileText />,
    name: "Disputes",
    path: "/disputes",
    permission: PERMISSIONS.DISPUTE_READ,
    requiresDisputesVisible: true,
  },
  {
    icon: <ChartColumn />,
    name: "Reports",
    path: "/reports",
    permission: [PERMISSIONS.REPORTS_ALL, PERMISSIONS.REPORTS_OWN],
  },
  {
    icon: <MessagesSquare />,
    name: "Inquiries",
    path: "/inquiries",
    permission: PERMISSIONS.DISPUTE_EVIDENCE_SUBMIT,
  },
  {
    icon: <PenLine />,
    name: "Evidence Builder",
    path: "/evidence-builder",
    permission: PERMISSIONS.DISPUTE_EVIDENCE,
  },
  {
    icon: <Search />,
    name: "Payment Search",
    path: "/payment-search",
    permission: PERMISSIONS.PAYMENT_SEARCH,
  },
  {
    icon: <Building2 />,
    name: "Onboarding",
    path: "/onboarding",
    permission: PERMISSIONS.COMPANIES_WRITE_ALL,
  },
  {
    icon: <Plug />,
    name: "Integrations",
    path: "/integrations",
    permission: PERMISSIONS.ACCOUNTS_OWN,
  },
  {
    icon: <Zap />,
    name: "Admin",
    subItems: [
      { name: "Panel", path: "/admin-panel", permission: PERMISSIONS.ADMIN_PANEL },
      { name: "Shopify Payments Cache", path: "/admin/shopify-payments-cache", permission: PERMISSIONS.ADMIN_PANEL },
      { name: "CheckoutChamp Transactions", path: "/admin/checkout-champ-transactions", permission: PERMISSIONS.ADMIN_PANEL },
      { name: "CRM Connector Transactions", path: "/admin/crm-connector-transactions", permission: PERMISSIONS.ADMIN_PANEL },
      { name: "Authorize.net Transactions", path: "/admin/authorize-net-transactions", permission: PERMISSIONS.ADMIN_PANEL },
      { name: "Evidence Activity", path: "/admin/evidence-activity", permission: PERMISSIONS.EVIDENCE_ACTIVITY },
    ],
    permission: [PERMISSIONS.ADMIN_PANEL, PERMISSIONS.EVIDENCE_ACTIVITY],
  },
  {
    icon: <Receipt />,
    name: "Billing",
    path: "/billing",
    permission: PERMISSIONS.BILLING_OWN,
  },
  {
    icon: <KeyRound />,
    name: "API Keys",
    path: "/developer/api-keys",
    permission: PERMISSIONS.API_KEYS_OWN,
  },
];

interface SidebarTooltipProps {
  title: string;
  children: React.ReactElement;
  isExpanded: boolean;
  isMobileOpen: boolean;
  disabled?: boolean;
}

const SidebarTooltip: React.FC<SidebarTooltipProps> = ({ title, children, isExpanded, isMobileOpen, disabled }) => {
  return (
    <Tooltip
      title={title}
      placement="right"
      disableHoverListener={isExpanded || isMobileOpen || disabled}
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.87)',
            color: 'white',
            fontSize: '14px',
            padding: '8px 12px',
            fontWeight: 500,
            boxShadow: '0px 4px 6px -2px rgba(0, 0, 0, 0.1), 0px 2px 4px -2px rgba(0, 0, 0, 0.06)',
          }
        }
      }}
    >
      {children}
    </Tooltip>
  );
};

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, toggleMobileSidebar, toggleSidebar } = useSidebar();
  const location = useLocation();
  const [{ user }] = useStateValue();
  const disputesVisible = useDisputesVisible();
  const orderInsightsVisible = useOrderInsightsVisible();

  // any-of semantics, mirroring the route <Protected> guard
  const matchesPermission = useCallback(
    (permission?: string | string[]) => {
      if (!permission) return true;
      return Array.isArray(permission)
        ? permission.some((p) => user?.permissions?.includes(p))
        : user?.permissions?.includes(permission);
    },
    [user]
  );

  const isNavVisible = useCallback(
    (nav: NavItem) => {
      if (!matchesPermission(nav.permission)) return false;
      if (nav.requiresDisputesVisible && !disputesVisible) return false;
      if (nav.requiresOrderInsightsVisible && !orderInsightsVisible) return false;
      return true;
    },
    [matchesPermission, disputesVisible, orderInsightsVisible]
  );

  // Group visible sub-items by their `section` (preserving order). Items without
  // a section form the unlabeled lead group. Used to render section headers +
  // dividers — but only when more than one group is visible (e.g. admin); a
  // single group (e.g. client) renders as a plain flat list.
  const groupSubItems = useCallback(
    (subItems: SubItem[]) => {
      const groups: { label: string; items: SubItem[] }[] = [];
      for (const item of subItems.filter((s) => matchesPermission(s.permission))) {
        const label = item.section ?? "";
        const group = groups.find((g) => g.label === label);
        if (group) group.items.push(item);
        else groups.push({ label, items: [item] });
      }
      return groups;
    },
    [matchesPermission]
  );

  const [openSubmenu, setOpenSubmenu] = useState<number | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {}
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Popover state for collapsed sidebar submenu access
  const [popoverAnchor, setPopoverAnchor] = useState<{
    element: HTMLElement | null;
    index: number;
    nav: NavItem;
  } | null>(null);
  // Pending close timer for the hover bridge between trigger and popover paper.
  const popoverCloseTimer = useRef<number | null>(null);

  const isActive = useCallback(
    (path: string) => {
      // Exact match
      if (location.pathname === path) return true;
      // Match sub-routes (e.g., /companies/6 matches /companies)
      if (path !== "/" && location.pathname.startsWith(path + "/")) return true;
      return false;
    },
    [location.pathname]
  );

  // True when the current route is one of a group's sub-pages. Used to highlight
  // the parent only when its open drawer isn't already showing the active child
  // (i.e. when collapsed, or when the drawer is closed in expanded mode) — so
  // there's always exactly one indicator and never a parent+child double-highlight.
  const isGroupActive = useCallback(
    (nav: NavItem) => nav.subItems?.some((subItem) => isActive(subItem.path)) ?? false,
    [isActive]
  );

  useEffect(() => {
    let submenuMatched = false;
    const filteredItems = navItems.filter(isNavVisible);
    filteredItems.forEach((nav, index) => {
      if (nav.subItems) {
        const hasActiveSubItem = nav.subItems.some((subItem) => isActive(subItem.path));
        if (hasActiveSubItem) {
          setOpenSubmenu(index);
          submenuMatched = true;
        }
      }
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location, isActive, user, isNavVisible]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `main-${openSubmenu}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number) => {
    setOpenSubmenu((prevOpenSubmenu) => (prevOpenSubmenu === index ? null : index));
  };

  const handleMenuItemClick = () => {
    if (isMobileOpen) {
      toggleMobileSidebar();
    }
  };

  const cancelPopoverClose = () => {
    if (popoverCloseTimer.current !== null) {
      clearTimeout(popoverCloseTimer.current);
      popoverCloseTimer.current = null;
    }
  };

  const handlePopoverClose = () => {
    cancelPopoverClose();
    setPopoverAnchor(null);
  };

  // Schedule a close that any subsequent hover (trigger or paper) can cancel,
  // so travelling between the icon and the popover doesn't bounce it shut.
  const schedulePopoverClose = () => {
    cancelPopoverClose();
    popoverCloseTimer.current = window.setTimeout(() => {
      popoverCloseTimer.current = null;
      setPopoverAnchor(null);
    }, 120);
  };

  const handleSubmenuMouseEnter = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
    nav: NavItem
  ) => {
    if (!isExpanded && !isMobileOpen && nav.subItems) {
      cancelPopoverClose();
      const element = event.currentTarget;
      // Don't re-anchor if this icon's popover is already open — a fresh object
      // would re-render and let MUI reposition, feeding the flicker loop.
      setPopoverAnchor((prev) =>
        prev && prev.index === index ? prev : { element, index, nav }
      );
    }
  };

  const handleSubmenuMouseLeave = () => {
    schedulePopoverClose();
  };

  const renderMenuItems = (items: NavItem[]) => (
    <ul className="flex flex-col gap-4">
      {items
        .filter(isNavVisible)
        .map((nav, index) => (
          <li key={nav.name}>
            {nav.subItems ? (
              <SidebarTooltip title={nav.name} isExpanded={isExpanded} isMobileOpen={isMobileOpen} disabled={!isExpanded && !isMobileOpen}>
                <button
                  onClick={() => handleSubmenuToggle(index)}
                  onMouseEnter={(e) => handleSubmenuMouseEnter(e, index, nav)}
                  onMouseLeave={handleSubmenuMouseLeave}
                  className={`menu-item group ${isGroupActive(nav) && ((!isExpanded && !isMobileOpen) || openSubmenu !== index)
                      ? "menu-item-active"
                      : "menu-item-inactive"
                    } cursor-pointer ${!isExpanded
                      ? "lg:justify-center"
                      : "lg:justify-start"
                    }`}
                >
                  <span className={`menu-item-icon-size ${isGroupActive(nav) && ((!isExpanded && !isMobileOpen) || openSubmenu !== index)
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                    }`}>
                    {nav.icon}
                  </span>
                  {(isExpanded || isMobileOpen) && (
                    <span className="menu-item-text">{nav.name}</span>
                  )}
                  {(isExpanded || isMobileOpen) && (
                    <ChevronDownIcon
                      className={`ml-auto w-5 h-5 transition-transform duration-200 ${openSubmenu === index
                          ? "rotate-180"
                          : ""
                        }`}
                    />
                  )}
                </button>
              </SidebarTooltip>
            ) : (
              nav.path && (
                <SidebarTooltip title={nav.name} isExpanded={isExpanded} isMobileOpen={isMobileOpen}>
                  <CmLink
                    to={nav.path}
                    onClick={handleMenuItemClick}
                    className={`menu-item group ${isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
                      }`}
                  >
                    <span
                      className={`menu-item-icon-size ${isActive(nav.path)
                          ? "menu-item-icon-active"
                          : "menu-item-icon-inactive"
                        }`}
                    >
                      {nav.icon}
                    </span>
                    {(isExpanded || isMobileOpen) && (
                      <span className="menu-item-text">{nav.name}</span>
                    )}
                  </CmLink>
                </SidebarTooltip>
              )
            )}
            {nav.subItems && (isExpanded || isMobileOpen) && (
              <div
                ref={(el) => {
                  subMenuRefs.current[`main-${index}`] = el;
                }}
                className="overflow-hidden transition-all duration-300"
                style={{
                  height:
                    openSubmenu === index
                      ? `${subMenuHeight[`main-${index}`]}px`
                      : "0px",
                }}
              >
                <div className="mt-2 ml-9">
                  {(() => {
                    const groups = groupSubItems(nav.subItems!);
                    const showSections = groups.length > 1;
                    return groups.map((group, groupIndex) => (
                      <div key={group.label || "_lead"}>
                        {showSections && groupIndex > 0 && (
                          <div className="my-2 border-t border-gray-100 dark:border-gray-800" />
                        )}
                        {showSections && group.label && (
                          <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                            {group.label}
                          </div>
                        )}
                        <ul className="space-y-0.5">
                          {group.items.map((subItem) => (
                    <li key={subItem.name}>
                      <SidebarTooltip title={subItem.name} isExpanded={isExpanded} isMobileOpen={isMobileOpen}>
                        <CmLink
                          to={subItem.path}
                          onClick={handleMenuItemClick}
                          className={`menu-dropdown-item ${isActive(subItem.path)
                              ? "menu-dropdown-item-active"
                              : "menu-dropdown-item-inactive"
                            }`}
                        >
                          {subItem.name}
                        <span className="flex items-center gap-1 ml-auto">
                          {subItem.new && (
                            <span
                              className={`ml-auto ${isActive(subItem.path)
                                  ? "menu-dropdown-badge-active"
                                  : "menu-dropdown-badge-inactive"
                                } menu-dropdown-badge`}
                            >
                              new
                            </span>
                          )}
                          {subItem.pro && (
                            <span
                              className={`ml-auto ${isActive(subItem.path)
                                  ? "menu-dropdown-badge-active"
                                  : "menu-dropdown-badge-inactive"
                                } menu-dropdown-badge`}
                            >
                              pro
                            </span>
                          )}
                        </span>
                      </CmLink>
                      </SidebarTooltip>
                    </li>
                          ))}
                        </ul>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
          </li>
        ))}
    </ul>
  );

  return (
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-[var(--app-banner-height)] px-5 left-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-[calc(100vh_-_var(--app-banner-height))] transition-[width,transform] duration-300 ease-in-out z-50 border-r border-gray-200
        ${isExpanded || isMobileOpen
          ? "w-[290px]"
          : "w-[90px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
    >
      <div
        className={`py-8 flex ${!isExpanded ? "lg:justify-center" : "justify-start"
          }`}
      >
        {menuHeader()}
      </div>
      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className="mb-6">
          <div className="flex flex-col gap-4">
            <div>
              <h2
                className={`mb-4 text-xs uppercase flex leading-[20px] text-gray-400 ${!isExpanded
                    ? "lg:justify-center"
                    : "justify-start"
                  }`}
              >
                {isExpanded || isMobileOpen ? (
                  "Menu"
                ) : (
                  <HorizontaLDots className="size-6" />
                )}
              </h2>
              {renderMenuItems(navItems)}
            </div>
          </div>
        </nav>
      </div>
      {/* Toggle Button */}
      <div className={`mt-auto pb-6 flex ${!isExpanded && !isMobileOpen ? "lg:justify-center" : "justify-start"}`}>
        <button
          onClick={() => {
            if (window.innerWidth >= 1024) {
              toggleSidebar();
            } else {
              toggleMobileSidebar();
            }
          }}
          className={`menu-item group menu-item-inactive cursor-pointer ${!isExpanded && !isMobileOpen
              ? "lg:justify-center"
              : "lg:justify-start"
            }`}
          aria-label={(isExpanded && !isMobileOpen) || isMobileOpen ? "Collapse sidebar" : "Expand sidebar"}
        >
          <span className="menu-item-icon-size menu-item-icon-inactive">
            {(isExpanded && !isMobileOpen) || isMobileOpen ? (
              <ChevronLeftIcon className="w-5 h-5" />
            ) : (
              <AngleRightIcon className="w-5 h-5" />
            )}
          </span>
          {(isExpanded || isMobileOpen) && (
            <span className="menu-item-text">
              {(isExpanded && !isMobileOpen) || isMobileOpen ? "Collapse" : "Expand"}
            </span>
          )}
        </button>
      </div>

      {/* Popover for collapsed sidebar submenu access (hover triggered) */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor?.element}
        onClose={handlePopoverClose}
        disableRestoreFocus
        disableScrollLock
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            onMouseEnter: cancelPopoverClose,
            onMouseLeave: handlePopoverClose,
            sx: {
              minWidth: 140,
              borderRadius: "12px",
              boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
              pointerEvents: "auto",
            },
          },
        }}
        sx={{
          pointerEvents: "none",
        }}
      >
        {popoverAnchor?.nav.subItems && (
          <div className="py-1">
            <div className="px-4 py-2 text-sm font-medium text-gray-900">
              {popoverAnchor.nav.name}
            </div>
            {groupSubItems(popoverAnchor.nav.subItems).map((group, groupIndex, groups) => (
              <div key={group.label || "_lead"}>
                {groups.length > 1 && groupIndex > 0 && (
                  <div className="my-1 border-t border-gray-100" />
                )}
                {groups.length > 1 && group.label && (
                  <div className="px-4 pt-1.5 pb-0.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {group.label}
                  </div>
                )}
                <ul>
                  {group.items.map((subItem) => (
                <li key={subItem.name}>
                  <CmLink
                    to={subItem.path}
                    onClick={handlePopoverClose}
                    className={`block px-4 py-2 text-sm transition-colors rounded-md mx-1 ${
                      isActive(subItem.path)
                        ? "bg-brand-50 text-brand-500 font-medium"
                        : "text-gray-700 hover:bg-gray-100 font-normal"
                    }`}
                  >
                    {subItem.name}
                  </CmLink>
                </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Popover>
    </aside>
  );

  function menuHeader() {
    if (isMobileOpen) {
      return null;
    }
    return <Link to="/">
      {isExpanded ? (
        <>
          <img
            src="/images/logo/Logo_Black.svg"
            alt="Logo"
            width={150}
            height={40} />
        </>
      ) : (
        <img
          src="/images/logo/Logo_White_Small.svg"
          alt="Logo"
          width={32}
          height={32} />
      )}
    </Link>;
  }
};

export default AppSidebar;
